import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

/**
 * 画像の元サイズ（オリジナル座標）
 */
export interface OriginalDimensions {
  width: number
  height: number
}

/**
 * 画像メトリクス（表示用の計算結果）
 */
export interface ImageMetrics {
  /** 表示幅 */
  dispW: number
  /** 表示高さ */
  dispH: number
  /** X方向のオフセット */
  offX: number
  /** Y方向のオフセット */
  offY: number
  /** コンテナ幅 */
  containerW: number
  /** コンテナ高さ */
  containerH: number
}

/**
 * visualViewport の変化を監視するhook
 * Android Chrome のアドレスバー表示/非表示による座標ズレを防ぐ
 */
const useVisualViewportListener = (onViewportChange: () => void) => {
  const callbackRef = useRef(onViewportChange)
  callbackRef.current = onViewportChange

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const handleViewportChange = () => {
      callbackRef.current()
    }

    // resize: ビューポートサイズ変化（アドレスバー表示/非表示含む）
    // scroll: ビューポートのスクロール位置変化
    vv.addEventListener('resize', handleViewportChange)
    vv.addEventListener('scroll', handleViewportChange)

    return () => {
      vv.removeEventListener('resize', handleViewportChange)
      vv.removeEventListener('scroll', handleViewportChange)
    }
  }, [])
}

interface UseImageMetricsOptions {
  containerRef: RefObject<HTMLElement>
  originalDimensions: OriginalDimensions | null
  dependencies?: unknown[]
}

/**
 * 画像のメトリクス（表示サイズと位置）を計算するhook
 * コンテナサイズに合わせて画像をアスペクト比を保ちながら表示するための計算を行う
 */
export const useImageMetrics = ({
  containerRef,
  originalDimensions,
  dependencies = [],
}: UseImageMetricsOptions) => {
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })

  /**
   * コンテナサイズを更新する
   * Android Chrome のアドレスバー変化時も呼び出される
   */
  const updateContainerSize = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setContainerSize(prev => {
        // 値が変わっていない場合は更新しない（不要な再レンダリング防止）
        if (prev.width === rect.width && prev.height === rect.height) {
          return prev
        }
        return { width: rect.width, height: rect.height }
      })
    }
  }, [containerRef])

  /**
   * 画像の表示サイズと位置を計算する
   */
  const getMetrics = (): ImageMetrics => {
    if (!originalDimensions) {
      return { dispW: 0, dispH: 0, offX: 0, offY: 0, containerW: 0, containerH: 0 }
    }
    const scale = Math.min(
      containerSize.width / originalDimensions.width,
      containerSize.height / originalDimensions.height,
    )
    const dispW = originalDimensions.width * scale
    const dispH = originalDimensions.height * scale
    const offX = (containerSize.width - dispW) / 2
    const offY = (containerSize.height - dispH) / 2
    return {
      dispW,
      dispH,
      offX,
      offY,
      containerW: containerSize.width,
      containerH: containerSize.height,
    }
  }

  // visualViewport の変化を監視（Android Chrome アドレスバー対応）
  useVisualViewportListener(updateContainerSize)

  useEffect(() => {
    updateContainerSize()

    // ResizeObserverを使用してコンテナサイズの変更を効率的に検出
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      updateContainerSize()
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [containerRef, updateContainerSize, ...dependencies])

  return {
    containerSize,
    getMetrics,
  }
}
